import type { MapDefinition } from '../../model/gamestate/GameState';
import { getGrid } from '../../grid';
import { World2DCamera, type ScreenPoint, type WorldPoint } from './camera';
import { buildStaticObjectRenderData } from './contracts';
import { getDefaultWorldAtlasSources } from './assets/defaultSources';
import type { WorldAtlasAssetProvider } from './assets/assetProvider';
import { PrebuiltWorldAtlasAssetProvider } from './assets/prebuiltAssetProvider';
import { StaticObjectLayer } from './layers/StaticObjectLayer';
import { DynamicEntityLayer, type DynamicHitTarget } from './layers/DynamicEntityLayer';
import { FogLayer } from './layers/FogLayer';
import { UiLayer } from './layers/UiLayer';
import type { DynamicSceneRenderData } from './contracts';
import type { TilePosition } from '../../model/gamestate/GameState';
import type { ActiveZoneData } from '../../spacetime/modules/SkillModule';
import type { SkillTargetingState } from '../../events/SkillTargetingEvents';
import {
  applyWorldLayerOverscanStyle,
  createOverlayLayer,
  createLayerCanvas,
  configureLayerContext,
  syncLayerCanvasSize,
  syncOverlayLayerSize,
} from './renderSurface';
import { TerrainSurfaceManager } from './TerrainSurfaceManager';
import { TerrainGpuLayer } from './TerrainGpuLayer';

declare global {
  interface Window {
    __tileMapPerfLog?: string;
    __tileMapPerfLineCount?: number;
    __enableTileMapPerf?: boolean;
  }
}

interface World2DRendererOptions {
  readonly container: HTMLElement;
  readonly map: MapDefinition;
  readonly assetProvider?: WorldAtlasAssetProvider;
  readonly basePixelsPerWorldUnit?: number;
}

interface MapObjectPlacementPreviewState {
  active: boolean;
  assetId: string | null;
  previewMode: 'tile-anchor' | 'map-anchor';
  tileX?: number;
  tileY?: number;
  mapX?: number;
  mapY?: number;
  offsetX?: number;
  offsetY?: number;
  sizeMultiplier: number;
  flipHorizontal: boolean;
  flipVertical: boolean;
  rotationSteps: number;
}

interface PathTilePlacementPreviewState {
  active: boolean;
  atlasId: string | null;
  tileIndex: number | null;
  autotile: boolean;
  flipHorizontal: boolean;
  flipVertical: boolean;
  rotationSteps: number;
}

interface BiomeRegionPlacementPreviewState {
  active: boolean;
  biome: string | null;
  startX?: number;
  startY?: number;
  endX?: number;
  endY?: number;
}

type DirtyLayerName = 'terrain' | 'objects' | 'dynamic' | 'fog' | 'ui';

const WORLD_LAYER_OVERSCAN_PX = 0;
const ZOOM_IN_SCENE_COMMIT_DELAY_MS = 50;
const ZOOM_IN_TERRAIN_PRIORITY_DELAY_MS = 120;
const ZOOM_IN_TERRAIN_FULL_DELAY_MS = 500;
const ZOOM_OUT_TERRAIN_PRIORITY_DELAY_MS = 80;
const ZOOM_OUT_TERRAIN_FULL_DELAY_MS = 280;

export class World2DRenderer {
  private readonly container: HTMLElement;
  private readonly root: HTMLDivElement;
  private readonly terrainHost: HTMLDivElement;
  private readonly terrainGpuLayer: TerrainGpuLayer;
  private readonly objectCanvas: LayerCanvas;
  private readonly animatedObjectOverlay: HTMLDivElement;
  private readonly dynamicCanvas: LayerCanvas;
  private readonly fogCanvas: LayerCanvas;
  private readonly uiCanvas: LayerCanvas;
  private readonly camera: World2DCamera;
  private readonly assetProvider: WorldAtlasAssetProvider;
  private readonly terrainManager: TerrainSurfaceManager;
  private readonly objectLayer: StaticObjectLayer;
  private readonly dynamicEntityLayer = new DynamicEntityLayer();
  private readonly fogLayer = new FogLayer();
  private readonly uiLayer: UiLayer;
  private readonly resizeObserver: ResizeObserver | null;
  private map: MapDefinition;
  private objectData: ReturnType<typeof buildStaticObjectRenderData>;
  private dynamicScene: DynamicSceneRenderData = { actors: [], markers: [] };
  private queuePreview: (TilePosition | null)[] = [];
  private activeZones: ActiveZoneData[] = [];
  private hoveredTile: TilePosition | null = null;
  private hoveredTileInFog = false;
  private skillTargetingActive = false;
  private skillTargetingState: SkillTargetingState | null = null;
  private hoveredActor: DynamicHitTarget | null = null;
  private hoverPointer: { x: number; y: number } | null = null;
  private selfPosition: WorldPoint | null = null;
  private activeRadius = 0;
  private visibilityMode: MapDefinition['visibilityMode'];
  private frameRequested = false;
  private terrainDirty = true;
  private objectsDirty = true;
  private dynamicDirty = true;
  private fogDirty = true;
  private uiDirty = true;
  private readonly dirtyReasons: Record<DirtyLayerName, string> = {
    terrain: 'init',
    objects: 'init',
    dynamic: 'init',
    fog: 'init',
    ui: 'init',
  };
  private pendingZoomFactor: number | null = null;
  private pendingZoomAnchor: ScreenPoint | null = null;
  private zoomCommitTimer: number | null = null;
  private terrainZoomCommitTimer: number | null = null;
  private terrainZoomFollowupTimer: number | null = null;
  private terrainBootstrapPollTimer: number | null = null;
  private terrainBootstrapPollsRemaining = 0;
  private deferredTerrainZoomReason: string | null = null;
  private pendingGpuSeedRender = false;
  private clearSceneTransientZoomAfterRender = false;
  private clearTerrainTransientZoomAfterRender = false;
  private zoomCommitListener: (() => void) | null = null;
  private constructor(options: World2DRendererOptions) {
    this.container = options.container;
    this.map = options.map;
    this.assetProvider = options.assetProvider ?? new PrebuiltWorldAtlasAssetProvider({
      sourceFactory: getDefaultWorldAtlasSources,
      manifestPath: `${import.meta.env.BASE_URL}visual-assets/world2d-atlas/default/manifest.json`,
    });
    this.root = document.createElement('div');
    this.root.style.position = 'absolute';
    this.root.style.inset = '0';
    this.root.style.overflow = 'hidden';
    this.root.style.zIndex = '0';

    this.terrainHost = createOverlayLayer(0);
    this.terrainGpuLayer = new TerrainGpuLayer();
    this.objectCanvas = createLayerCanvas(1);
    this.animatedObjectOverlay = createOverlayLayer(2);
    this.dynamicCanvas = createLayerCanvas(3);
    this.fogCanvas = createLayerCanvas(4);
    this.uiCanvas = createLayerCanvas(5, true);
    this.objectLayer = new StaticObjectLayer(this.animatedObjectOverlay, () => {
      this.markObjectsDirty('asset');
      this.requestRender();
    });
    this.uiLayer = new UiLayer(() => {
      this.markUiDirty('asset');
      this.requestRender();
    });

    this.root.append(
      this.terrainHost,
      this.objectCanvas.canvas,
      this.animatedObjectOverlay,
      this.dynamicCanvas.canvas,
      this.fogCanvas.canvas,
      this.uiCanvas.canvas,
    );
    this.container.appendChild(this.root);
    this.terrainHost.appendChild(this.terrainGpuLayer.getElement());
    applyWorldLayerOverscanStyle(this.objectCanvas.canvas, WORLD_LAYER_OVERSCAN_PX);
    applyWorldLayerOverscanStyle(this.animatedObjectOverlay, WORLD_LAYER_OVERSCAN_PX);
    applyWorldLayerOverscanStyle(this.dynamicCanvas.canvas, WORLD_LAYER_OVERSCAN_PX);
    applyWorldLayerOverscanStyle(this.fogCanvas.canvas, WORLD_LAYER_OVERSCAN_PX);

    this.camera = new World2DCamera({
      viewportWidthPx: Math.max(1, this.container.clientWidth),
      viewportHeightPx: Math.max(1, this.container.clientHeight),
      basePixelsPerWorldUnit: options.basePixelsPerWorldUnit,
      centerX: options.map.width / 2,
      centerY: options.map.height / 2,
    });
    this.terrainManager = new TerrainSurfaceManager(
      this.map,
      () => this.requestRender(),
      (reason) => this.markTerrainDirty(reason),
    );
    this.objectData = buildStaticObjectRenderData(this.map);
    this.visibilityMode = this.map.visibilityMode;
    this.camera.setCenter({ x: this.map.width / 2, y: this.map.height / 2 }, getGrid(this.map.gridType));

    if (typeof ResizeObserver === 'undefined') {
      this.resizeObserver = null;
    } else {
      this.resizeObserver = new ResizeObserver(() => {
        this.syncCanvasSizes();
        this.requestRender();
      });
      this.resizeObserver.observe(this.container);
    }

    this.startTerrainBootstrapPolling();
  }

  public static async create(options: World2DRendererOptions): Promise<World2DRenderer> {
    const renderer = new World2DRenderer(options);
    if (typeof window !== 'undefined') {
      if (typeof window.__tileMapPerfLog !== 'string') {
        window.__tileMapPerfLog = '';
      }
      if (typeof window.__tileMapPerfLineCount !== 'number') {
        window.__tileMapPerfLineCount = 0;
      }
    }
    await renderer.assetProvider.ensureReady();
    renderer.syncCanvasSizes();
    renderer.requestRender();
    return renderer;
  }

  public destroy(): void {
    this.cancelTransientZoom();
    if (this.terrainBootstrapPollTimer !== null) {
      window.clearTimeout(this.terrainBootstrapPollTimer);
      this.terrainBootstrapPollTimer = null;
    }
    this.terrainBootstrapPollsRemaining = 0;
    this.resizeObserver?.disconnect();
    this.terrainManager.destroy();
    this.terrainGpuLayer.destroy();
    this.root.remove();
  }

  public get view(): HTMLCanvasElement {
    return this.uiCanvas.canvas;
  }

  public getViewSize(): { width: number; height: number } {
    return {
      width: this.container.clientWidth,
      height: this.container.clientHeight,
    };
  }

  public setMap(map: MapDefinition): void {
    this.cancelTransientZoom();
    this.map = map;
    this.objectData = buildStaticObjectRenderData(map);
    this.visibilityMode = map.visibilityMode;
    this.terrainManager.setMap(map);
    this.camera.setCenter({ x: map.width / 2, y: map.height / 2 }, getGrid(map.gridType));
    this.markAllLayersDirty('map');
    this.startTerrainBootstrapPolling();
    this.requestRender();
  }

  public setZoomFactor(zoomFactor: number): void {
    const currentEffectiveZoom = this.getEffectiveZoomFactor();
    this.pendingZoomFactor = zoomFactor;
    this.pendingZoomAnchor = null;
    this.applyTransientZoomTransform();
    if (zoomFactor < currentEffectiveZoom) {
      this.commitTransientZoomIfNeeded();
      return;
    }
    this.scheduleZoomCommit(ZOOM_IN_SCENE_COMMIT_DELAY_MS);
  }

  public zoomAboutScreenPoint(
    zoomFactor: number,
    anchor: ScreenPoint,
    grid: ReturnType<typeof getGrid>,
    mapWidth: number,
    mapHeight: number,
  ): void {
    const currentEffectiveZoom = this.getEffectiveZoomFactor();
    this.pendingZoomFactor = zoomFactor;
    this.pendingZoomAnchor = { ...anchor };
    this.applyTransientZoomTransform();
    if (zoomFactor < currentEffectiveZoom) {
      this.commitTransientZoomIfNeeded();
      return;
    }
    this.scheduleZoomCommit(ZOOM_IN_SCENE_COMMIT_DELAY_MS);
  }

  public panByScreenDelta(deltaX: number, deltaY: number): void {
    this.commitTransientZoomIfNeeded();
    this.flushDeferredTerrainZoom();
    this.camera.panByScreenDelta(deltaX, deltaY);
    this.markAllLayersDirty('pan');
    this.requestRender();
  }

  public setCameraCenter(center: WorldPoint): void {
    const currentCenter = this.camera.getCenter();
    if (Math.abs(currentCenter.x - center.x) < 0.0001 && Math.abs(currentCenter.y - center.y) < 0.0001) {
      return;
    }
    this.flushDeferredTerrainZoom();
    this.camera.setCenter(center, getGrid(this.map.gridType));
    this.markAllLayersDirty('camera-center');
    this.requestRender();
  }

  public setCameraWorldCenter(center: WorldPoint): void {
    const currentCenter = this.camera.getWorldCenter();
    if (Math.abs(currentCenter.x - center.x) < 0.01 && Math.abs(currentCenter.y - center.y) < 0.01) {
      return;
    }
    this.flushDeferredTerrainZoom();
    this.camera.setCenterFromWorldPoint(center);
    this.markAllLayersDirty('camera-center');
    this.requestRender();
  }

  public getCamera(): World2DCamera {
    return this.camera;
  }

  public getEffectiveZoomFactor(): number {
    return this.pendingZoomFactor ?? this.camera.getZoomFactor();
  }

  public isZoomTransitionActive(): boolean {
    return this.pendingZoomFactor !== null
      || this.zoomCommitTimer !== null
      || this.terrainZoomCommitTimer !== null
      || this.clearSceneTransientZoomAfterRender
      || this.clearTerrainTransientZoomAfterRender;
  }

  public setZoomCommitListener(listener: (() => void) | null): void {
    this.zoomCommitListener = listener;
  }

  public setSelfPosition(position: WorldPoint | null): void {
    if (
      (this.selfPosition === null && position === null)
      || (this.selfPosition !== null && position !== null && this.selfPosition.x === position.x && this.selfPosition.y === position.y)
    ) {
      return;
    }
    this.selfPosition = position;
    this.markDynamicDirty('self-position');
    this.markFogDirty('self-position');
    this.markUiDirty('self-position');
    this.requestRender();
  }

  public setDynamicScene(scene: DynamicSceneRenderData): void {
    if (this.dynamicScene === scene) {
      return;
    }
    this.dynamicScene = scene;
    this.markDynamicDirty('dynamic-scene');
    this.requestRender();
  }

  public setQueuePreview(queue: (TilePosition | null)[]): void {
    if (areTilePositionListsEqual(this.queuePreview, queue)) {
      return;
    }
    this.queuePreview = queue.slice();
    this.markUiDirty('queue-preview');
    this.requestRender();
  }

  public setActiveZones(zones: ActiveZoneData[]): void {
    if (areActiveZonesEqual(this.activeZones, zones)) {
      return;
    }
    this.activeZones = zones.slice();
    this.markUiDirty('active-zones');
    this.requestRender();
  }

  public setHoveredTile(tile: TilePosition | null, inFog: boolean): void {
    if (
      this.hoveredTileInFog === inFog
      && (
        (this.hoveredTile === null && tile === null)
        || (this.hoveredTile !== null && tile !== null && this.hoveredTile.x === tile.x && this.hoveredTile.y === tile.y)
      )
    ) {
      return;
    }
    this.hoveredTile = tile ? { ...tile } : null;
    this.hoveredTileInFog = inFog;
    this.markUiDirty('hovered-tile');
    this.requestRender();
  }

  public setSkillTargetingActive(active: boolean): void {
    if (this.skillTargetingActive === active) {
      return;
    }
    this.skillTargetingActive = active;
    this.markUiDirty('targeting-active');
    this.requestRender();
  }

  public setSkillTargetingState(state: SkillTargetingState | null): void {
    this.skillTargetingState = state
      ? {
          active: state.active,
          skillId: state.skillId,
          requiredSlots: state.requiredSlots,
          slotTypes: [...state.slotTypes],
          hovered: state.hovered ? { ...state.hovered, position: { ...state.hovered.position } } : null,
          selected: state.selected.map((entry) => (entry ? { ...entry, position: { ...entry.position } } : null)),
        }
      : null;
    this.markUiDirty('targeting-state');
    this.requestRender();
  }

  public setHoveredActor(actor: DynamicHitTarget | null, pointer: { x: number; y: number } | null): void {
    if (
      this.hoveredActor === actor
      && (
        (this.hoverPointer === null && pointer === null)
        || (this.hoverPointer !== null && pointer !== null && this.hoverPointer.x === pointer.x && this.hoverPointer.y === pointer.y)
      )
    ) {
      return;
    }
    this.hoveredActor = actor;
    this.hoverPointer = pointer ? { ...pointer } : null;
    this.markUiDirty('hovered-actor');
    this.requestRender();
  }

  public setMapObjectPlacementPreview(preview: MapObjectPlacementPreviewState): void {
    this.uiLayer.setMapObjectPlacementPreview(preview);
    this.markUiDirty('object-preview');
    this.requestRender();
  }

  public setPathTilePlacementPreview(preview: PathTilePlacementPreviewState): void {
    this.uiLayer.setPathTilePlacementPreview(preview);
    this.markUiDirty('path-preview');
    this.requestRender();
  }

  public setBiomeRegionPlacementPreview(preview: BiomeRegionPlacementPreviewState): void {
    this.uiLayer.setBiomeRegionPlacementPreview(preview);
    this.markUiDirty('biome-preview');
    this.requestRender();
  }

  public setActiveRadius(activeRadius: number): void {
    const nextActiveRadius = Number.isFinite(activeRadius) ? Math.max(0, activeRadius) : 0;
    if (this.activeRadius === nextActiveRadius) {
      return;
    }
    this.activeRadius = nextActiveRadius;
    this.markFogDirty('active-radius');
    this.markUiDirty('active-radius');
    this.requestRender();
  }

  private markTerrainDirty(reason = 'unknown'): void {
    this.terrainDirty = true;
    this.dirtyReasons.terrain = reason;
  }

  private markObjectsDirty(reason = 'unknown'): void {
    this.objectsDirty = true;
    this.dirtyReasons.objects = reason;
  }

  private markDynamicDirty(reason = 'unknown'): void {
    this.dynamicDirty = true;
    this.dirtyReasons.dynamic = reason;
  }

  private markFogDirty(reason = 'unknown'): void {
    this.fogDirty = true;
    this.dirtyReasons.fog = reason;
  }

  private markUiDirty(reason = 'unknown'): void {
    this.uiDirty = true;
    this.dirtyReasons.ui = reason;
  }

  private markAllLayersDirty(reason = 'unknown'): void {
    this.markTerrainDirty(reason);
    this.markObjectsDirty(reason);
    this.markDynamicDirty(reason);
    this.markFogDirty(reason);
    this.markUiDirty(reason);
  }

  public requestRender(): void {
    if (this.frameRequested) {
      return;
    }
    this.frameRequested = true;
    requestAnimationFrame(() => {
      this.frameRequested = false;
      this.render();
    });
  }

  private render(): void {
    const frameStart = performance.now();
    const grid = getGrid(this.map.gridType);

    let setupMs = 0;
    const renderedDirtyReasons: string[] = [];

    let terrainMs = 0;
    let terrainPresentMs = 0;
    if (this.terrainDirty) {
      renderedDirtyReasons.push(`terrain:${this.dirtyReasons.terrain}`);
      const terrainStart = performance.now();
      this.terrainManager.draw(this.camera, this.dirtyReasons.terrain);
      const terrainPresentStart = performance.now();
      this.syncTerrainPresentation();
      terrainPresentMs = performance.now() - terrainPresentStart;
      terrainMs = performance.now() - terrainStart;
      this.terrainDirty = false;
      this.dirtyReasons.terrain = 'clean';
    }

    let objectsMs = 0;
    if (this.objectsDirty) {
      renderedDirtyReasons.push(`objects:${this.dirtyReasons.objects}`);
      const setupStart = performance.now();
      configureLayerContext(this.objectCanvas, this.container, WORLD_LAYER_OVERSCAN_PX);
      setupMs += performance.now() - setupStart;
      const objectsStart = performance.now();
      this.objectLayer.draw(this.objectCanvas.ctx, this.camera, this.objectData, this.assetProvider, this.map.gridType, WORLD_LAYER_OVERSCAN_PX);
      objectsMs = performance.now() - objectsStart;
      this.objectsDirty = false;
      this.dirtyReasons.objects = 'clean';
    }

    let dynamicMs = 0;
    if (this.dynamicDirty) {
      renderedDirtyReasons.push(`dynamic:${this.dirtyReasons.dynamic}`);
      const setupStart = performance.now();
      configureLayerContext(this.dynamicCanvas, this.container, WORLD_LAYER_OVERSCAN_PX);
      setupMs += performance.now() - setupStart;
      const dynamicStart = performance.now();
      this.dynamicEntityLayer.draw(this.dynamicCanvas.ctx, this.camera, this.dynamicScene, grid, WORLD_LAYER_OVERSCAN_PX);
      dynamicMs = performance.now() - dynamicStart;
      this.dynamicDirty = false;
      this.dirtyReasons.dynamic = 'clean';
    }

    let fogMs = 0;
    if (this.fogDirty) {
      renderedDirtyReasons.push(`fog:${this.dirtyReasons.fog}`);
      const setupStart = performance.now();
      configureLayerContext(this.fogCanvas, this.container, WORLD_LAYER_OVERSCAN_PX);
      setupMs += performance.now() - setupStart;
      const fogStart = performance.now();
      this.fogLayer.draw(this.fogCanvas.ctx, this.camera, {
        visibilityMode: this.visibilityMode,
        selfPosition: this.selfPosition,
        activeRadius: this.activeRadius,
        mapWidth: this.map.width,
        mapHeight: this.map.height,
        gridType: this.map.gridType,
      }, WORLD_LAYER_OVERSCAN_PX);
      fogMs = performance.now() - fogStart;
      this.fogDirty = false;
      this.dirtyReasons.fog = 'clean';
    }

    let uiMs = 0;
    if (this.uiDirty) {
      renderedDirtyReasons.push(`ui:${this.dirtyReasons.ui}`);
      const setupStart = performance.now();
      configureLayerContext(this.uiCanvas, this.container);
      setupMs += performance.now() - setupStart;
      const uiStart = performance.now();
      this.uiLayer.setQueuePreview(this.queuePreview);
      this.uiLayer.setActiveZones(this.activeZones);
      this.uiLayer.setHoveredTile(this.hoveredTile, this.hoveredTileInFog);
      this.uiLayer.setSkillTargetingActive(this.skillTargetingActive);
      this.uiLayer.setSkillTargetingState(this.skillTargetingState);
      this.uiLayer.setHoveredActor(this.hoveredActor, this.hoverPointer);
      this.uiLayer.draw(
        this.uiCanvas.ctx,
        this.camera,
        grid,
        this.map,
        this.assetProvider,
        this.selfPosition ? { x: this.selfPosition.x, y: this.selfPosition.y } : null,
        this.activeRadius,
      );
      uiMs = performance.now() - uiStart;
      this.uiDirty = false;
      this.dirtyReasons.ui = 'clean';
    }

    const totalFrameMs = performance.now() - frameStart;
    if ((typeof window !== 'undefined' && window.__enableTileMapPerf === true) && totalFrameMs >= 8) {
      const manifest = this.assetProvider.getManifest();
      const terrainStats = this.terrainManager.getDebugStats();
      const perfLine =
        `[Perf][World2DFrame] total=${(Math.round(totalFrameMs * 10) / 10).toFixed(1)}ms `
        + `grid=${this.map.gridType} `
        + `zoom=${this.camera.getZoomFactor().toFixed(2)} `
        + `ppu=${(Math.round(this.camera.getPixelsPerWorldUnit() * 10) / 10).toFixed(1)} `
        + `terrainLevel=${terrainStats.levelZoom.toFixed(2)} `
        + `terrainTarget=${terrainStats.targetLevelZoom.toFixed(2)} `
        + `terrainResident=${terrainStats.usedResidentLevel ? 'fallback' : 'exact'} `
        + `terrainLevels=${terrainStats.residentLevels} `
        + `terrainResidentChunks=${terrainStats.residentChunks} `
        + `atlas=${manifest?.source ?? 'unknown'} `
        + `pages=${manifest?.pages.length ?? 0} `
        + `dirty=${renderedDirtyReasons.length > 0 ? renderedDirtyReasons.join(',') : 'none'} `
        + `setup=${(Math.round(setupMs * 10) / 10).toFixed(1)}ms `
        + `terrain=${(Math.round(terrainMs * 10) / 10).toFixed(1)}ms `
        + `(rebuild=${(Math.round(terrainStats.rebuildMs * 10) / 10).toFixed(1)} `
        + `compose=${(Math.round(terrainStats.drawMs * 10) / 10).toFixed(1)} `
        + `present=${(Math.round(terrainPresentMs * 10) / 10).toFixed(1)} `
        + `chunks=${terrainStats.visibleChunks}/${terrainStats.rebuiltChunks} `
        + `chunkTiles=${terrainStats.chunkTileSize} `
        + `blits=${terrainStats.drawBlitCount} `
        + `blitArea=${Math.round(terrainStats.drawBlitArea)} `
        + `blitMax=${Math.round(terrainStats.maxBlitArea)} `
        + `base=${(Math.round(terrainStats.terrainBaseMs * 10) / 10).toFixed(1)} `
        + `path=${(Math.round(terrainStats.pathTileLayerMs * 10) / 10).toFixed(1)}) `
        + `objects=${(Math.round(objectsMs * 10) / 10).toFixed(1)}ms `
        + `dynamic=${(Math.round(dynamicMs * 10) / 10).toFixed(1)}ms `
        + `fog=${(Math.round(fogMs * 10) / 10).toFixed(1)}ms `
        + `ui=${(Math.round(uiMs * 10) / 10).toFixed(1)}ms`;
      console.log(perfLine);
      appendPerfLogLine(perfLine);
    }

    if (this.clearSceneTransientZoomAfterRender) {
      this.clearSceneTransientZoomAfterRender = false;
      this.clearSceneTransientZoomTransform();
    }
    if (this.clearTerrainTransientZoomAfterRender) {
      this.clearTerrainTransientZoomAfterRender = false;
      this.clearTerrainTransientZoomTransform();
    }
  }

  private syncCanvasSizes(): void {
    this.cancelTransientZoom();
    const width = Math.max(1, this.container.clientWidth);
    const height = Math.max(1, this.container.clientHeight);
    this.camera.setViewportSize(width, height);
    syncOverlayLayerSize(this.terrainHost, width, height);
    this.terrainManager.syncCanvasDimensions(width, height);
    this.terrainGpuLayer.syncSize(width, height);
    syncLayerCanvasSize(this.objectCanvas.canvas, width, height, WORLD_LAYER_OVERSCAN_PX);
    syncOverlayLayerSize(this.animatedObjectOverlay, width, height, WORLD_LAYER_OVERSCAN_PX);
    syncLayerCanvasSize(this.dynamicCanvas.canvas, width, height, WORLD_LAYER_OVERSCAN_PX);
    syncLayerCanvasSize(this.fogCanvas.canvas, width, height, WORLD_LAYER_OVERSCAN_PX);
    syncLayerCanvasSize(this.uiCanvas.canvas, width, height);
    this.markAllLayersDirty('resize');
  }

  public hitTestDynamicEntity(screenX: number, screenY: number): DynamicHitTarget | null {
    return this.dynamicEntityLayer.hitTest(screenX, screenY);
  }

  private scheduleZoomCommit(delayMs: number): void {
    if (this.zoomCommitTimer !== null) {
      window.clearTimeout(this.zoomCommitTimer);
    }
    this.zoomCommitTimer = window.setTimeout(() => {
      this.zoomCommitTimer = null;
      this.commitTransientZoomIfNeeded();
    }, delayMs);
  }

  private commitTransientZoomIfNeeded(): void {
    if (this.pendingZoomFactor === null) {
      return;
    }
    if (this.zoomCommitTimer !== null) {
      window.clearTimeout(this.zoomCommitTimer);
      this.zoomCommitTimer = null;
    }
    const zoomFactor = this.pendingZoomFactor;
    const anchor = this.pendingZoomAnchor;
    const previousZoomFactor = this.camera.getZoomFactor();
    this.pendingZoomFactor = null;
    this.pendingZoomAnchor = null;
    const zoomReason = anchor ? 'zoom-anchor' : 'zoom';
    if (anchor) {
      this.camera.zoomAboutScreenPoint(zoomFactor, anchor, getGrid(this.map.gridType), this.map.width, this.map.height);
    } else {
      this.camera.setZoomFactor(zoomFactor);
    }
    if (zoomFactor > previousZoomFactor) {
      this.markObjectsDirty(zoomReason);
      this.markDynamicDirty(zoomReason);
      this.markFogDirty(zoomReason);
      this.markUiDirty(zoomReason);
      this.scheduleTerrainZoomCommits(zoomReason, ZOOM_IN_TERRAIN_PRIORITY_DELAY_MS, ZOOM_IN_TERRAIN_FULL_DELAY_MS);
      this.clearSceneTransientZoomAfterRender = true;
    } else {
      this.markObjectsDirty(zoomReason);
      this.markDynamicDirty(zoomReason);
      this.markFogDirty(zoomReason);
      this.markUiDirty(zoomReason);
      this.scheduleTerrainZoomCommits(zoomReason, ZOOM_OUT_TERRAIN_PRIORITY_DELAY_MS, ZOOM_OUT_TERRAIN_FULL_DELAY_MS);
      this.clearSceneTransientZoomAfterRender = true;
    }
    this.zoomCommitListener?.();
    this.requestRender();
  }

  private cancelTransientZoom(): void {
    this.pendingZoomFactor = null;
    this.pendingZoomAnchor = null;
    if (this.zoomCommitTimer !== null) {
      window.clearTimeout(this.zoomCommitTimer);
      this.zoomCommitTimer = null;
    }
    if (this.terrainZoomCommitTimer !== null) {
      window.clearTimeout(this.terrainZoomCommitTimer);
      this.terrainZoomCommitTimer = null;
    }
    if (this.terrainZoomFollowupTimer !== null) {
      window.clearTimeout(this.terrainZoomFollowupTimer);
      this.terrainZoomFollowupTimer = null;
    }
    this.deferredTerrainZoomReason = null;
    this.clearSceneTransientZoomAfterRender = false;
    this.clearTerrainTransientZoomAfterRender = false;
    this.clearTransientZoomTransform();
  }

  private applyTransientZoomTransform(): void {
    const pendingZoomFactor = this.pendingZoomFactor;
    if (pendingZoomFactor === null) {
      this.clearTransientZoomTransform();
      return;
    }
    const scale = pendingZoomFactor / this.camera.getZoomFactor();
    const viewport = this.camera.getViewportSize();
    const anchor = this.pendingZoomAnchor ?? { x: viewport.width / 2, y: viewport.height / 2 };
    for (const element of this.getZoomTransformElements()) {
      element.style.transformOrigin = `${anchor.x}px ${anchor.y}px`;
      element.style.transform = `scale(${scale})`;
    }
  }

  private clearTransientZoomTransform(): void {
    for (const element of this.getZoomTransformElements()) {
      element.style.transform = '';
      element.style.transformOrigin = '';
    }
  }

  private clearSceneTransientZoomTransform(): void {
    for (const element of this.getSceneZoomTransformElements()) {
      element.style.transform = '';
      element.style.transformOrigin = '';
    }
  }

  private clearTerrainTransientZoomTransform(): void {
    this.terrainHost.style.transform = '';
    this.terrainHost.style.transformOrigin = '';
  }

  private getZoomTransformElements(): HTMLElement[] {
    return [
      this.terrainHost,
      ...this.getSceneZoomTransformElements(),
    ];
  }

  private getSceneZoomTransformElements(): HTMLElement[] {
    return [
      this.objectCanvas.canvas,
      this.animatedObjectOverlay,
      this.dynamicCanvas.canvas,
      this.fogCanvas.canvas,
      this.uiCanvas.canvas,
    ];
  }

  private syncTerrainPresentation(): void {
    const gpuCommands = this.terrainManager.getGpuDrawCommands();
    const presentationCanvas = this.terrainManager.getPresentationCanvas();
    if (gpuCommands) {
      const gpuMode = this.terrainManager.getGpuPresentationMode();
      if (gpuMode === 'patch' && !this.terrainGpuLayer.isSeeded()) {
        this.terrainGpuLayer.clear();
        this.terrainHost.replaceChildren();
        if (presentationCanvas) {
          this.terrainHost.appendChild(presentationCanvas);
        }
        if (!this.pendingGpuSeedRender) {
          this.pendingGpuSeedRender = true;
          this.markTerrainDirty('gpu-seed');
          this.requestRender();
        }
        return;
      }
      this.pendingGpuSeedRender = false;
      this.terrainGpuLayer.render(gpuCommands, gpuMode);
      const gpuElement = this.terrainGpuLayer.getElement();
      if (this.terrainHost.firstElementChild !== gpuElement) {
        this.terrainHost.replaceChildren(gpuElement);
      }
      return;
    }
    this.pendingGpuSeedRender = false;
    this.terrainGpuLayer.clear();
    const currentCanvas = this.terrainHost.firstElementChild;
    if (currentCanvas === presentationCanvas) {
      return;
    }
    this.terrainHost.replaceChildren();
    if (presentationCanvas) {
      this.terrainHost.appendChild(presentationCanvas);
    }
  }

  private scheduleTerrainZoomCommits(baseReason: string, priorityDelayMs: number, fullDelayMs: number): void {
    if (this.terrainZoomCommitTimer !== null) {
      window.clearTimeout(this.terrainZoomCommitTimer);
    }
    if (this.terrainZoomFollowupTimer !== null) {
      window.clearTimeout(this.terrainZoomFollowupTimer);
      this.terrainZoomFollowupTimer = null;
    }
    this.deferredTerrainZoomReason = baseReason;
    this.terrainZoomCommitTimer = window.setTimeout(() => {
      this.terrainZoomCommitTimer = null;
      if (!this.deferredTerrainZoomReason) {
        return;
      }
      this.markTerrainDirty(`${this.deferredTerrainZoomReason}-priority`);
      this.clearTerrainTransientZoomAfterRender = true;
      this.requestRender();
      this.terrainZoomFollowupTimer = window.setTimeout(() => {
        this.terrainZoomFollowupTimer = null;
        if (!this.deferredTerrainZoomReason) {
          return;
        }
        this.markTerrainDirty(`${this.deferredTerrainZoomReason}-full`);
        this.deferredTerrainZoomReason = null;
        this.requestRender();
      }, fullDelayMs);
    }, priorityDelayMs);
  }

  private flushDeferredTerrainZoom(): void {
    if (this.terrainZoomCommitTimer !== null) {
      window.clearTimeout(this.terrainZoomCommitTimer);
      this.terrainZoomCommitTimer = null;
    }
    if (this.terrainZoomFollowupTimer !== null) {
      window.clearTimeout(this.terrainZoomFollowupTimer);
      this.terrainZoomFollowupTimer = null;
    }
    if (!this.deferredTerrainZoomReason) {
      return;
    }
    this.markTerrainDirty(this.deferredTerrainZoomReason);
    this.deferredTerrainZoomReason = null;
    this.clearTerrainTransientZoomAfterRender = true;
  }

  private startTerrainBootstrapPolling(): void {
    if (this.terrainBootstrapPollTimer !== null) {
      window.clearTimeout(this.terrainBootstrapPollTimer);
      this.terrainBootstrapPollTimer = null;
    }
    this.terrainBootstrapPollsRemaining = 12;
    this.scheduleNextTerrainBootstrapPoll();
  }

  private scheduleNextTerrainBootstrapPoll(): void {
    if (this.terrainBootstrapPollsRemaining <= 0) {
      this.terrainBootstrapPollTimer = null;
      return;
    }
    this.terrainBootstrapPollTimer = window.setTimeout(() => {
      this.terrainBootstrapPollTimer = null;
      this.terrainBootstrapPollsRemaining -= 1;
      this.terrainManager.invalidateAllLevelTextures();
      const currentViewport = this.camera.getViewportSize();
      const width = Math.max(1, this.container.clientWidth);
      const height = Math.max(1, this.container.clientHeight);
      if (currentViewport.width !== width || currentViewport.height !== height) {
        this.syncCanvasSizes();
      }
      this.markTerrainDirty('startup-refresh');
      this.requestRender();
      this.scheduleNextTerrainBootstrapPoll();
    }, 200);
  }
}

function appendPerfLogLine(line: string): void {
  if (typeof window === 'undefined') {
    return;
  }
  const current = window.__tileMapPerfLog;
  let next = current ? `${current}\n${line}` : line;
  let lineCount = (window.__tileMapPerfLineCount ?? (current ? current.split('\n').length : 0)) + 1;
  while (lineCount > 80) {
    const firstNewline = next.indexOf('\n');
    if (firstNewline < 0) {
      lineCount = 1;
      break;
    }
    next = next.slice(firstNewline + 1);
    lineCount -= 1;
  }
  window.__tileMapPerfLog = next;
  window.__tileMapPerfLineCount = lineCount;
}

function areTilePositionListsEqual(
  left: readonly (TilePosition | null)[],
  right: readonly (TilePosition | null)[],
): boolean {
  if (left.length !== right.length) {
    return false;
  }
  for (let index = 0; index < left.length; index += 1) {
    const a = left[index];
    const b = right[index];
    if (a === b) {
      continue;
    }
    if (!a || !b || a.x !== b.x || a.y !== b.y) {
      return false;
    }
  }
  return true;
}

function areActiveZonesEqual(
  left: readonly ActiveZoneData[],
  right: readonly ActiveZoneData[],
): boolean {
  if (left.length !== right.length) {
    return false;
  }
  for (let index = 0; index < left.length; index += 1) {
    const a = left[index];
    const b = right[index];
    if (
      a.zoneType !== b.zoneType
      || a.centerX !== b.centerX
      || a.centerY !== b.centerY
      || a.radius !== b.radius
    ) {
      return false;
    }
  }
  return true;
}
